import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

export default function Contact() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSubmitting(true);
    // Frontend-only: no API call. Redirect to thank you.
    setTimeout(() => navigate("/thank_you"), 250);
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative overflow-hidden" data-testid="section-contact">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow">{t("contact.eyebrow")}</div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-4 font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#0F172A]"
          >
            {t("contact.title")}
          </motion.h2>
          <p className="mt-6 text-[#525866] max-w-md leading-relaxed">{t("contact.lead")}</p>

          <div className="mt-10 space-y-3">
            <a
              href="mailto:hola@ronavirona.com"
              className="font-serif-display text-2xl link-underline text-[#0F172A] block"
              data-testid="contact-email"
            >
              {t("contact.email_link")}
            </a>
            <a
              href="tel:+541152194820"
              className="font-mono-mark text-sm tracking-[0.15em] uppercase text-[#525866] link-underline"
              data-testid="contact-phone"
            >
              {t("contact.phone_link")}
            </a>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={onSubmit}
          className="lg:col-span-6 lg:col-start-7 border border-slate-900/10 bg-white rounded-md p-6 md:p-10"
          data-testid="contact-form"
          noValidate
        >
          <FormField
            id="name"
            name="name"
            label={t("contact.name_label")}
            placeholder={t("contact.name_placeholder")}
            value={form.name}
            onChange={onChange}
            testId="contact-name-input"
            required
          />
          <FormField
            id="email"
            name="email"
            type="email"
            label={t("contact.email_label")}
            placeholder={t("contact.email_placeholder")}
            value={form.email}
            onChange={onChange}
            testId="contact-email-input"
            required
          />
          <FormField
            id="message"
            name="message"
            label={t("contact.message_label")}
            placeholder={t("contact.message_placeholder")}
            value={form.message}
            onChange={onChange}
            testId="contact-message-input"
            textarea
            required
          />
          <div className="mt-6 flex items-center justify-between gap-4">
            <p className="text-xs text-[#525866]">
              {t("footer.disclaimer").split(".")[0]}.
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="btn-pill btn-pill--dark"
              data-testid="contact-form-submit-button"
            >
              {t("contact.submit")} <span aria-hidden>→</span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function FormField({ id, name, label, placeholder, value, onChange, testId, textarea, type = "text", required }) {
  return (
    <div className="mt-5 first:mt-0">
      <label htmlFor={id} className="eyebrow block mb-2">
        {label}
      </label>
      {textarea ? (
        <textarea
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={5}
          required={required}
          data-testid={testId}
          className="w-full bg-transparent border-b border-slate-900/20 focus:border-[#D97706] outline-none py-3 text-[#0F172A] placeholder:text-[#525866]/60 transition-colors resize-none"
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
          className="w-full bg-transparent border-b border-slate-900/20 focus:border-[#D97706] outline-none py-3 text-[#0F172A] placeholder:text-[#525866]/60 transition-colors"
        />
      )}
    </div>
  );
}
